import React, { useEffect, useMemo, useRef, useState } from "react";
import theme from "../../../../../styles/theme";
import drawSpaceConfig from "../config/drawSpaceConfig";
import { buildGridLines } from "../utils/drawGrid";
import symbolRegistry from "../spatial/symbols/symbolRegistry";



const snapPoint = (point, gridSize, snapToGrid) => {
  if (!snapToGrid) return point;

  return {
    x: Math.round(point.x / gridSize) * gridSize,
    y: Math.round(point.y / gridSize) * gridSize,
  };
};

const getCursor = (tool, isPanning, selectedId) => {
  if (isPanning) return "grabbing";
  if (tool === drawSpaceConfig.tools.PAN) return "grab";

  if (
    tool === drawSpaceConfig.tools.MOVE ||
    tool === drawSpaceConfig.tools.COPY
  ) {
    return selectedId ? "crosshair" : "default";
  }

  if (
    tool === drawSpaceConfig.tools.AREA_RECT ||
    tool === drawSpaceConfig.tools.AREA_POLYLINE ||
    tool === drawSpaceConfig.tools.LINE ||
    tool === drawSpaceConfig.tools.CIRCLE ||
    tool === drawSpaceConfig.tools.POINT
  ) {
    return "crosshair";
  }

  return "default";
};

const isCreationTool = (tool) => {
  return (
    tool === drawSpaceConfig.tools.AREA_RECT ||
    tool === drawSpaceConfig.tools.AREA_POLYLINE ||
    tool === drawSpaceConfig.tools.LINE ||
    tool === drawSpaceConfig.tools.CIRCLE ||
    tool === drawSpaceConfig.tools.POINT
  );
};

const getModeLabel = (activeMode) => {
  if (activeMode === "SITE") return "Mode : Site";
  if (activeMode === "OUVRAGE") return "Mode : Ouvrage";
  if (activeMode === "NIVEAU") return "Mode : Level";
  return "Mode : -";
};

const getActivePalette = (activeMode) => {
  if (activeMode === "SITE") return drawSpaceConfig.palettes.ouvrage;
  if (activeMode === "OUVRAGE") return drawSpaceConfig.palettes.level;
  return drawSpaceConfig.palettes.area;
};

const getPaletteByKey = (paletteKey, fallbackPalette) => {
  if (!paletteKey) return fallbackPalette;
  return drawSpaceConfig.palettes[paletteKey] ?? fallbackPalette;
};

const DrawViewport = ({
  width,
  height,

  entities = [],
  interfaceObjects = [],
  selectedId,
  selectedInterfaceId = null,

  zoom,
  offsetX,
  offsetY,

  showGrid = true,
  gridSize = 20,
  snapToGrid = true,

  screenToWorld,
  zoomAtPoint,

  tool,
  setTool,

  selectEntity,
  selectInterfaceObject = () => {},
  clearSelection,
  clearInterfaceSelection = () => {},
  createRectangle,
  createInterfaceRectangle = () => {},
  createCircle,
  createLine,
  createPolyline,
  createPoint,
  moveEntityInContext,
  copyEntityInContext,
  deleteEntityInContext,
  panViewport,

  activeMode = "SITE",
  warningMessage = null,

  style = {},
}) => {
  const svgRef = useRef(null);

  const [viewportSize, setViewportSize] = useState({
    width: 1000,
    height: typeof height === "number" ? height : 600,
  });

  const [panStart, setPanStart] = useState(null);
  const [drawStart, setDrawStart] = useState(null);
  const [lineStart, setLineStart] = useState(null);
  const [polyPoints, setPolyPoints] = useState([]);
  const [hoverPoint, setHoverPoint] = useState(null);
  const [hoverScreenPoint, setHoverScreenPoint] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const [moveState, setMoveState] = useState({
    active: false,
    entityId: null,
    basePoint: null,
  });
  const [copyState, setCopyState] = useState({
  active: false,
  entityId: null,
  basePoint: null,
});
  const startCopy = (entityId, basePoint) => {
  setCopyState({
    active: true,
    entityId,
    basePoint,
  });
};

const clearCopyState = () => {
  setCopyState({
    active: false,
    entityId: null,
    basePoint: null,
  });
};
  const getCopyPreviewDelta = () => {
  if (
    tool !== drawSpaceConfig.tools.COPY ||
    !copyState.active ||
    !copyState.basePoint ||
    !hoverPoint
  ) {
    return { dx: 0, dy: 0 };
  }

  return {
    dx: hoverPoint.x - copyState.basePoint.x,
    dy: hoverPoint.y - copyState.basePoint.y,
  };
};

  const suppressClickRef = useRef(false);

  const viewportWidth = viewportSize.width;
  const viewportHeight = viewportSize.height;

  const activePalette = useMemo(() => {
    return getActivePalette(activeMode);
  }, [activeMode]);

  useEffect(() => {
    if (!svgRef.current) return;

    const updateSize = () => {
      const rect = svgRef.current.getBoundingClientRect();

      setViewportSize({
        width: rect.width || 1000,
        height: rect.height || (typeof height === "number" ? height : 600),
      });
    };

    updateSize();

    const observer = new ResizeObserver(() => {
      updateSize();
    });

    observer.observe(svgRef.current);
    window.addEventListener("resize", updateSize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateSize);
    };
  }, [height]);

  const grid = useMemo(
    () =>
      buildGridLines({
        width: viewportWidth,
        height: viewportHeight,
        gridSize,
        offsetX,
        offsetY,
        zoom,
      }),
    [viewportWidth, viewportHeight, gridSize, offsetX, offsetY, zoom]
  );

  const clearDraftState = () => {
    setDrawStart(null);
    setLineStart(null);
    setPolyPoints([]);
  };
  const clearMoveState = () => {
    setMoveState({
      active: false,
      entityId: null,
      basePoint: null,
    });
  };
  const getMovePreviewDelta = () => {
  if (
    tool !== drawSpaceConfig.tools.MOVE ||
    !moveState.active ||
    !moveState.basePoint ||
    !hoverPoint
  ) {
    return { dx: 0, dy: 0 };
  }

  return {
    dx: hoverPoint.x - moveState.basePoint.x,
    dy: hoverPoint.y - moveState.basePoint.y,
  };
};
  
  const getPointerData = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;

    const rawWorld = screenToWorld(sx, sy);
    const world = snapPoint(rawWorld, gridSize, snapToGrid);

    return {
      sx,
      sy,
      rawWorld,
      world,
    };
  };

  const getDraftReferencePoint = () => {
  if (tool === drawSpaceConfig.tools.AREA_RECT && drawStart) return drawStart;
  if (tool === drawSpaceConfig.tools.CIRCLE && drawStart) return drawStart;
  if (tool === drawSpaceConfig.tools.LINE && lineStart) return lineStart;

  if (
    tool === drawSpaceConfig.tools.AREA_POLYLINE &&
    Array.isArray(polyPoints) &&
    polyPoints.length > 0
  ) {
    return polyPoints[polyPoints.length - 1];
  }

  return null;
};

  const finalizePolyline = () => {
    if (polyPoints.length >= 3) {
      createPolyline(polyPoints);
    }
    setPolyPoints([]);
  };

  const finalizeLineWithHover = () => {
    if (!lineStart || !hoverPoint) return;

    createLine({
      x1: lineStart.x,
      y1: lineStart.y,
      x2: hoverPoint.x,
      y2: hoverPoint.y,
    });

    setLineStart(null);
  };

 

  const handleMouseDown = (e) => {
  const { world, rawWorld } = getPointerData(e);

  if (e.button === 1) {
    e.preventDefault();
    setIsPanning(true);
    setPanStart(rawWorld);
    return;
  }

  if (e.button !== 0) return;

  if (
    tool === drawSpaceConfig.tools.AREA_RECT ||
    tool === drawSpaceConfig.tools.INTERFACE_RECTANGLE ||
    tool === drawSpaceConfig.tools.CIRCLE
  ) {
    clearSelection();
  }

  if (tool === drawSpaceConfig.tools.AREA_RECT) {
    setDrawStart(world);
    return;
  }

  if (tool === drawSpaceConfig.tools.INTERFACE_RECTANGLE) {
    setDrawStart(world);
    return;
  }

  if (tool === drawSpaceConfig.tools.CIRCLE) {
    setDrawStart(world);
    return;
  }

  if (tool === drawSpaceConfig.tools.PAN) {
    setIsPanning(true);
    setPanStart(rawWorld);
    return;
  }

  if (tool === drawSpaceConfig.tools.SELECT) {
    clearSelection();
    clearDraftState();
  }
};

  const handleMouseUp = (e) => {
  if (e.button !== 0 && e.button !== 1) return;

  const { world } = getPointerData(e);

  if (tool === drawSpaceConfig.tools.AREA_RECT && drawStart) {
    const widthValue = world.x - drawStart.x;
    const heightValue = world.y - drawStart.y;

    if (Math.abs(widthValue) > 0 || Math.abs(heightValue) > 0) {
      createRectangle({
        x: drawStart.x,
        y: drawStart.y,
        width: widthValue,
        height: heightValue,
      });
    }

    setDrawStart(null);
    return;
  }

  if (tool === drawSpaceConfig.tools.INTERFACE_RECTANGLE && drawStart) {
    const widthValue = world.x - drawStart.x;
    const heightValue = world.y - drawStart.y;

    if (Math.abs(widthValue) > 0 || Math.abs(heightValue) > 0) {
      createInterfaceRectangle({
        x: drawStart.x,
        y: drawStart.y,
        width: widthValue,
        height: heightValue,
      });
    }

    setDrawStart(null);
    return;
  }

  if (tool === drawSpaceConfig.tools.CIRCLE && drawStart) {
    const dx = world.x - drawStart.x;
    const dy = world.y - drawStart.y;
    const r = Math.sqrt(dx * dx + dy * dy);

    if (r > 0) {
      createCircle({
        cx: drawStart.x,
        cy: drawStart.y,
        r,
      });
    }

    setDrawStart(null);
    return;
  }

  if (isPanning) {
    setIsPanning(false);
    setPanStart(null);
  }
};

  const handleMouseMove = (e) => {
    const { sx, sy, world, rawWorld } = getPointerData(e);

    setHoverPoint(world);
    setHoverScreenPoint({ x: sx, y: sy });

    if (isPanning && panStart) {
      const dx = rawWorld.x - panStart.x;
      const dy = rawWorld.y - panStart.y;

      panViewport(dx * zoom, dy * zoom);
      setPanStart(rawWorld);
    }
  };

  const handleMouseLeave = () => {
    setHoverPoint(null);
    setHoverScreenPoint(null);
    setIsPanning(false);
    setPanStart(null);
  };

  const handleWheel = (e) => {
    if (!zoomAtPoint) return;

    e.preventDefault();

    const rect = svgRef.current.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;

    zoomAtPoint(sx, sy, e.deltaY);
  };

 useEffect(() => {
  const handleWindowMouseUp = () => {
    setIsPanning(false);
    setPanStart(null);
  };

 const handleKeyDown = (e) => {
  if (e.key === "Escape") {
    clearDraftState();
    clearMoveState();
    clearCopyState();
    setHoverPoint(null);
    setHoverScreenPoint(null);
    suppressClickRef.current = false;

    if (setTool) {
      setTool(drawSpaceConfig.tools.SELECT);
    }
    return;
  }

  if (e.key === "Enter") {
    if (tool === drawSpaceConfig.tools.DELETE && selectedId) {
      deleteEntityInContext(selectedId);
      return;
    }

    if (tool === drawSpaceConfig.tools.AREA_POLYLINE) {
      finalizePolyline();
      return;
    }

    if (tool === drawSpaceConfig.tools.LINE) {
      finalizeLineWithHover();
      return;
    }
  }

  if (
    tool === drawSpaceConfig.tools.DELETE &&
    (e.key === "Delete" || e.key === "Backspace") &&
    selectedId
  ) {
    deleteEntityInContext(selectedId);
  }
};

window.addEventListener("mouseup", handleWindowMouseUp);
window.addEventListener("keydown", handleKeyDown);

return () => {
  window.removeEventListener("mouseup", handleWindowMouseUp);
  window.removeEventListener("keydown", handleKeyDown);
};
}, [tool, lineStart, hoverPoint, polyPoints, setTool]);

 useEffect(() => {
  clearMoveState();

  if (tool === drawSpaceConfig.tools.SELECT) {
    clearDraftState();
  }
}, [tool]);

  const handleCanvasClick = (e) => {
  if (e.button !== 0) return;
    if (tool === drawSpaceConfig.tools.DELETE) {
  if (!selectedId) return;

  deleteEntityInContext(selectedId);
  return;
}
  if (suppressClickRef.current) {
    suppressClickRef.current = false;
    return;
  }

  if (tool === drawSpaceConfig.tools.SELECT && selectedInterfaceId) {
    clearInterfaceSelection();
  }

  const { world } = getPointerData(e);
  if (tool === drawSpaceConfig.tools.COPY) {
  if (!selectedId) return;

  if (!copyState.active) {
    startCopy(selectedId, world);
  } else {
    const dx = world.x - copyState.basePoint.x;
    const dy = world.y - copyState.basePoint.y;

    copyEntityInContext(copyState.entityId, dx, dy);
    clearCopyState();
  }

  return;
}

  if (tool === drawSpaceConfig.tools.MOVE) {
    if (!selectedId) return;

    if (!moveState.active || moveState.entityId !== selectedId) {
      setMoveState({
        active: true,
        entityId: selectedId,
        basePoint: world,
      });
      return;
    }

    if (moveState.basePoint) {
      const dx = world.x - moveState.basePoint.x;
      const dy = world.y - moveState.basePoint.y;

      if (dx || dy) {
        moveEntityInContext?.(moveState.entityId, dx, dy);
      }

      clearMoveState();
    }

    return;
  }

  if (tool === drawSpaceConfig.tools.POINT) {
    clearSelection();

    createPoint({
      x: world.x,
      y: world.y,
    });

    return;
  }

  if (tool === drawSpaceConfig.tools.LINE) {
    clearSelection();

    if (!lineStart) {
      setLineStart(world);
    } else {
      createLine({
        x1: lineStart.x,
        y1: lineStart.y,
        x2: world.x,
        y2: world.y,
      });
      setLineStart(null);
    }
    return;
  }

  if (tool === drawSpaceConfig.tools.AREA_POLYLINE) {
    clearSelection();
    setPolyPoints((prev) => [...prev, world]);
  }
};

  const handleContextMenu = (e) => {
  if (tool === drawSpaceConfig.tools.AREA_POLYLINE) {
    e.preventDefault();
    finalizePolyline();
  }

  if (tool === drawSpaceConfig.tools.LINE && lineStart) {
    e.preventDefault();
    finalizeLineWithHover();
  }
};

  const handleEntityClick = (e, entity) => {
  if (!entity || !entity.selectable) return;

  e.stopPropagation();
  selectEntity(entity.id);
};

  const renderRect = (entity) => {
  const g = entity.geometry;
  const selected = entity.id === selectedId;
  const palette = getPaletteByKey(entity.paletteKey, activePalette);

  const isCopyPreviewActive =
    tool === drawSpaceConfig.tools.COPY &&
    copyState.active &&
    copyState.entityId === entity.id;

  const copyPreview = isCopyPreviewActive
    ? getCopyPreviewDelta()
    : { dx: 0, dy: 0 };

  const x = Math.min(g.x, g.x + g.width);
  const y = Math.min(g.y, g.y + g.height);
  const rectWidth = Math.abs(g.width);
  const rectHeight = Math.abs(g.height);

  const previewX = Math.min(g.x + copyPreview.dx, g.x + g.width + copyPreview.dx);
  const previewY = Math.min(g.y + copyPreview.dy, g.y + g.height + copyPreview.dy);

  return (
    <g key={entity.id}>
      <rect
        x={x * zoom + offsetX}
        y={y * zoom + offsetY}
        width={rectWidth * zoom}
        height={rectHeight * zoom}
        fill={palette.fill}
        stroke={selected ? palette.selectedStroke : palette.stroke}
        strokeWidth={selected ? 2 : 1}
        onClick={(e) => handleEntityClick(e, entity)}
      />

      {isCopyPreviewActive && (
        <rect
          x={previewX * zoom + offsetX}
          y={previewY * zoom + offsetY}
          width={rectWidth * zoom}
          height={rectHeight * zoom}
          fill={activePalette.previewFill}
          stroke={activePalette.previewStroke}
          strokeWidth={1.5}
          strokeDasharray="6 4"
          pointerEvents="none"
        />
      )}
    </g>
  );
};

  const renderCircle = (entity) => {
  const g = entity.geometry;
  const selected = entity.id === selectedId;
  const palette = getPaletteByKey(entity.paletteKey, activePalette);

  const movePreview =
    moveState.active && moveState.entityId === entity.id
      ? getMovePreviewDelta()
      : { dx: 0, dy: 0 };

  const isCopyPreviewActive =
    tool === drawSpaceConfig.tools.COPY &&
    copyState.active &&
    copyState.entityId === entity.id;

  const copyPreview = isCopyPreviewActive
    ? getCopyPreviewDelta()
    : { dx: 0, dy: 0 };

  return (
    <g key={entity.id}>
      <circle
        cx={(g.cx + movePreview.dx) * zoom + offsetX}
        cy={(g.cy + movePreview.dy) * zoom + offsetY}
        r={Math.abs(g.r) * zoom}
        fill={palette.fill}
        stroke={selected ? palette.selectedStroke : palette.stroke}
        strokeWidth={selected ? 2 : 1}
        onClick={(e) => handleEntityClick(e, entity)}
      />

      {isCopyPreviewActive && (
        <circle
          cx={(g.cx + copyPreview.dx) * zoom + offsetX}
          cy={(g.cy + copyPreview.dy) * zoom + offsetY}
          r={Math.abs(g.r) * zoom}
          fill={activePalette.previewFill}
          stroke={activePalette.previewStroke}
          strokeWidth={1.5}
          strokeDasharray="6 4"
          pointerEvents="none"
        />
      )}
    </g>
  );
};

const renderPoint = (entity) => {
  const g = entity.geometry;
  const selected = entity.id === selectedId;
  const palette = getPaletteByKey(
    entity.paletteKey,
    drawSpaceConfig.palettes.point
  );

  const movePreview =
    moveState.active && moveState.entityId === entity.id
      ? getMovePreviewDelta()
      : { dx: 0, dy: 0 };

  const isCopyPreviewActive =
    tool === drawSpaceConfig.tools.COPY &&
    copyState.active &&
    copyState.entityId === entity.id;

  const copyPreview = isCopyPreviewActive
    ? getCopyPreviewDelta()
    : { dx: 0, dy: 0 };

  const x = (g.x + movePreview.dx) * zoom + offsetX;
  const y = (g.y + movePreview.dy) * zoom + offsetY;

  const previewX = (g.x + copyPreview.dx) * zoom + offsetX;
  const previewY = (g.y + copyPreview.dy) * zoom + offsetY;

  const symbol = symbolRegistry[entity.display?.symbolId];
  const isSymbol =
    entity.display?.type === drawSpaceConfig.displayTypes.SYMBOL;

  if (isSymbol && symbol) {
    return (
      <g key={entity.id}>
        <g
          transform={`translate(${x}, ${y})`}
          onClick={(e) => handleEntityClick(e, entity)}
        >
          {symbol.render({
            size: 12,
            color: selected ? palette.selectedStroke : palette.stroke,
          })}
        </g>

        {isCopyPreviewActive && (
          <g
            transform={`translate(${previewX}, ${previewY})`}
            pointerEvents="none"
          >
            {symbol.render({
              size: 12,
              color: activePalette.previewStroke,
            })}
          </g>
        )}
      </g>
    );
  }

  return (
    <g key={entity.id}>
      <circle
        cx={x}
        cy={y}
        r={drawSpaceConfig.defaults.defaultPointRadius || 6}
        fill={palette.fill}
        stroke={selected ? palette.selectedStroke : palette.stroke}
        strokeWidth={selected ? 2 : 1}
        onClick={(e) => handleEntityClick(e, entity)}
      />

      {isCopyPreviewActive && (
        <circle
          cx={previewX}
          cy={previewY}
          r={drawSpaceConfig.defaults.defaultPointRadius || 6}
          fill={activePalette.previewFill}
          stroke={activePalette.previewStroke}
          strokeWidth={0.5}
          strokeDasharray="6 4"
          pointerEvents="none"
        />
      )}
    </g>
  );
};

const renderLine = (entity) => {
  const g = entity.geometry;
  const selected = entity.id === selectedId;
  const palette = getPaletteByKey(entity.paletteKey, activePalette);

  const movePreview =
    moveState.active && moveState.entityId === entity.id
      ? getMovePreviewDelta()
      : { dx: 0, dy: 0 };

  const isCopyPreviewActive =
    tool === drawSpaceConfig.tools.COPY &&
    copyState.active &&
    copyState.entityId === entity.id;

  const copyPreview = isCopyPreviewActive
    ? getCopyPreviewDelta()
    : { dx: 0, dy: 0 };

  const x1 = (g.x1 + movePreview.dx) * zoom + offsetX;
  const y1 = (g.y1 + movePreview.dy) * zoom + offsetY;
  const x2 = (g.x2 + movePreview.dx) * zoom + offsetX;
  const y2 = (g.y2 + movePreview.dy) * zoom + offsetY;

  const previewX1 = (g.x1 + copyPreview.dx) * zoom + offsetX;
  const previewY1 = (g.y1 + copyPreview.dy) * zoom + offsetY;
  const previewX2 = (g.x2 + copyPreview.dx) * zoom + offsetX;
  const previewY2 = (g.y2 + copyPreview.dy) * zoom + offsetY;

  return (
    <g key={entity.id}>
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={selected ? palette.selectedStroke : palette.stroke}
        strokeWidth={selected ? 2.5 : 1.5}
        pointerEvents="none"
      />

      {(entity.selectable || entity.contextMovable) && (
        <line
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="transparent"
          strokeWidth={10}
          onClick={(e) => handleEntityClick(e, entity)}
        />
      )}

      {isCopyPreviewActive && (
        <line
          x1={previewX1}
          y1={previewY1}
          x2={previewX2}
          y2={previewY2}
          stroke={activePalette.previewStroke}
          strokeWidth={1.5}
          strokeDasharray="6 4"
          pointerEvents="none"
        />
      )}
    </g>
  );
};

const renderPolyline = (entity) => {
  const points = entity.geometry?.points ?? [];
  const selected = entity.id === selectedId;
  const palette = getPaletteByKey(entity.paletteKey, activePalette);

  const movePreview =
    moveState.active && moveState.entityId === entity.id
      ? getMovePreviewDelta()
      : { dx: 0, dy: 0 };

  const isCopyPreviewActive =
    tool === drawSpaceConfig.tools.COPY &&
    copyState.active &&
    copyState.entityId === entity.id;

  const copyPreview = isCopyPreviewActive
    ? getCopyPreviewDelta()
    : { dx: 0, dy: 0 };

  const screenPoints = points
    .map(
      (p) =>
        `${(p.x + movePreview.dx) * zoom + offsetX},${(p.y + movePreview.dy) * zoom + offsetY}`
    )
    .join(" ");

  const previewScreenPoints = points
    .map(
      (p) =>
        `${(p.x + copyPreview.dx) * zoom + offsetX},${(p.y + copyPreview.dy) * zoom + offsetY}`
    )
    .join(" ");

  return (
    <g key={entity.id}>
      <polygon
        points={screenPoints}
        fill={palette.fill}
        stroke={selected ? palette.selectedStroke : palette.stroke}
        strokeWidth={selected ? 2 : 1.5}
        onClick={(e) => handleEntityClick(e, entity)}
      />

      {isCopyPreviewActive && (
        <polygon
          points={previewScreenPoints}
          fill={activePalette.previewFill}
          stroke={activePalette.previewStroke}
          strokeWidth={1.5}
          strokeDasharray="6 4"
          pointerEvents="none"
        />
      )}
    </g>
  );
};

const renderInterfaceObject = (item) => {
  if (!item || item.InterfaceType !== "RECTANGLE") return null;

  const g = item.GeometryData;
  if (!g) return null;

  const x = Math.min(g.x, g.x + g.width);
  const y = Math.min(g.y, g.y + g.height);
  const rectWidth = Math.abs(g.width);
  const rectHeight = Math.abs(g.height);

  const isSelected = item.InterfaceID === selectedInterfaceId;

  return (
    <rect
      key={item.InterfaceID}
      x={x * zoom + offsetX}
      y={y * zoom + offsetY}
      width={rectWidth * zoom}
      height={rectHeight * zoom}
      fill="transparent"
      stroke={isSelected ? "#0f766e" : "#000"}
      strokeWidth={isSelected ? 2 : 1}
      onClick={(e) => {
        e.stopPropagation();
        selectInterfaceObject(item.InterfaceID);
      }}
    />
  );
};

  const renderEntity = (entity) => {
  if (!entity?.geometry) return null;

  if (entity.type === drawSpaceConfig.entityTypes.AREA_RECT) {
    return renderRect(entity);
  }

  if (entity.type === drawSpaceConfig.entityTypes.CIRCLE) {
    return renderCircle(entity);
  }

  if (entity.type === drawSpaceConfig.entityTypes.LINE) {
    return renderLine(entity);
  }

  if (entity.type === drawSpaceConfig.entityTypes.AREA_POLYLINE) {
    return renderPolyline(entity);
  }

  if (entity.type === drawSpaceConfig.entityTypes.POINT) {
    return renderPoint(entity);
  }

  return null;
};

  const renderDraft = () => {
  if (!hoverPoint) return null;

  if (
    (tool === drawSpaceConfig.tools.AREA_RECT ||
      tool === drawSpaceConfig.tools.INTERFACE_RECTANGLE) &&
    drawStart
  ) {
    const x = Math.min(drawStart.x, hoverPoint.x);
    const y = Math.min(drawStart.y, hoverPoint.y);
    const rectWidth = Math.abs(hoverPoint.x - drawStart.x);
    const rectHeight = Math.abs(hoverPoint.y - drawStart.y);

    return (
      <rect
        x={x * zoom + offsetX}
        y={y * zoom + offsetY}
        width={rectWidth * zoom}
        height={rectHeight * zoom}
        fill={activePalette.previewFill}
        stroke={activePalette.previewStroke}
        strokeWidth={1.5}
        strokeDasharray="6 4"
      />
    );
  }

  if (tool === drawSpaceConfig.tools.CIRCLE && drawStart) {
    const dx = hoverPoint.x - drawStart.x;
    const dy = hoverPoint.y - drawStart.y;
    const r = Math.sqrt(dx * dx + dy * dy);

    return (
      <circle
        cx={drawStart.x * zoom + offsetX}
        cy={drawStart.y * zoom + offsetY}
        r={Math.abs(r) * zoom}
        fill={activePalette.previewFill}
        stroke={activePalette.previewStroke}
        strokeWidth={1.5}
        strokeDasharray="6 4"
      />
    );
  }

  if (tool === drawSpaceConfig.tools.LINE && lineStart) {
    return (
      <line
        x1={lineStart.x * zoom + offsetX}
        y1={lineStart.y * zoom + offsetY}
        x2={hoverPoint.x * zoom + offsetX}
        y2={hoverPoint.y * zoom + offsetY}
        stroke={activePalette.previewStroke}
        strokeWidth={1.5}
        strokeDasharray="6 4"
      />
    );
  }

  if (tool === drawSpaceConfig.tools.AREA_POLYLINE && polyPoints.length > 0) {
    const screenPoints = [...polyPoints, hoverPoint]
      .map((p) => `${p.x * zoom + offsetX},${p.y * zoom + offsetY}`)
      .join(" ");

    return (
      <polyline
        points={screenPoints}
        fill="none"
        stroke={activePalette.previewStroke}
        strokeWidth={1.5}
        strokeDasharray="6 4"
      />
    );
  }

  return null;
};

    const draftReferencePoint = getDraftReferencePoint();
  const showCreationMarker =
  (isCreationTool(tool) ||
    (tool === drawSpaceConfig.tools.MOVE && !!selectedId) ||
    (tool === drawSpaceConfig.tools.COPY && !!selectedId)) &&
  !!hoverPoint;

const showFloatingInfo =
  (isCreationTool(tool) ||
    tool === drawSpaceConfig.tools.MOVE ||
    tool === drawSpaceConfig.tools.COPY) &&
  !!hoverPoint &&
  !!hoverScreenPoint;

  const floatingDelta =
  tool === drawSpaceConfig.tools.MOVE &&
  moveState.active &&
  moveState.basePoint &&
  hoverPoint
    ? {
        dx: hoverPoint.x - moveState.basePoint.x,
        dy: hoverPoint.y - moveState.basePoint.y,
      }
    : tool === drawSpaceConfig.tools.COPY &&
      copyState.active &&
      copyState.basePoint &&
      hoverPoint
    ? {
        dx: hoverPoint.x - copyState.basePoint.x,
        dy: hoverPoint.y - copyState.basePoint.y,
      }
    : null;

const floatingText = floatingDelta
  ? `ΔX: ${floatingDelta.dx.toFixed(1)} | ΔY: ${floatingDelta.dy.toFixed(1)}`
  : draftReferencePoint
  ? `ΔX: ${(hoverPoint.x - draftReferencePoint.x).toFixed(1)} | ΔY: ${(hoverPoint.y - draftReferencePoint.y).toFixed(1)}`
  : `X: ${hoverPoint?.x?.toFixed(1) ?? "-"} | Y: ${hoverPoint?.y?.toFixed(1) ?? "-"}`;
  
const renderVectorGuide = () => {
  const isMoveGuideActive =
    tool === drawSpaceConfig.tools.MOVE &&
    moveState.active &&
    moveState.basePoint &&
    hoverPoint;

  const isCopyGuideActive =
    tool === drawSpaceConfig.tools.COPY &&
    copyState.active &&
    copyState.basePoint &&
    hoverPoint;

  const basePoint = isMoveGuideActive
    ? moveState.basePoint
    : isCopyGuideActive
    ? copyState.basePoint
    : null;

  if (!basePoint) return null;

  return (
    <g pointerEvents="none">
      <line
        x1={basePoint.x * zoom + offsetX}
        y1={basePoint.y * zoom + offsetY}
        x2={hoverPoint.x * zoom + offsetX}
        y2={hoverPoint.y * zoom + offsetY}
        stroke={activePalette.previewStroke}
        strokeWidth={0.5}
        strokeDasharray="6 4"
      />
      <circle
        cx={basePoint.x * zoom + offsetX}
        cy={basePoint.y * zoom + offsetY}
        r={markerRadius}
        fill={markerColor}
        stroke="#4c5157"
        strokeWidth={0.5}
      />
    </g>
  );
};

const statusModeLabel = getModeLabel(activeMode);

  const statusCoordinates = hoverPoint
    ? `X: ${hoverPoint.x.toFixed(1)} | Y: ${hoverPoint.y.toFixed(1)}`
    : "X: - | Y: -";

  const moveStatus =
  tool === drawSpaceConfig.tools.MOVE
    ? !selectedId
      ? "Move : sélectionner une entité"
      : moveState.active
      ? "Move : cliquer le point cible"
      : "Move : cliquer le point de base"
    : tool === drawSpaceConfig.tools.COPY
    ? !selectedId
      ? "Copy : sélectionner une entité"
      : copyState.active
      ? "Copy : cliquer le point cible"
      : "Copy : cliquer le point de base"
    : "";
const markerColor =
  tool === drawSpaceConfig.tools.COPY
    ? "#4c5157"
    : tool === drawSpaceConfig.tools.MOVE
    ? "#4c5157"
    : activePalette.previewStroke;


    const markerRadius = 2;
  return (
    <div
      style={{
        width,
        height: "100%",
        background: theme.colors.background.panel,
        position: "relative",
        ...style,
      }}
    >
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleCanvasClick}
        onContextMenu={handleContextMenu}
        onWheel={handleWheel}
        style={{
          cursor: getCursor(tool, isPanning, selectedId),
          display: "block",
        }}
      >
        {showGrid &&
          grid.minor.map((line, index) => (
            <line
              key={`minor-${index}`}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke={theme.colors.borders.panel}
              strokeWidth={0.5}
            />
          ))}

        {showGrid &&
          grid.major.map((line, index) => (
            <line
              key={`major-${index}`}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke={theme.colors.borders.default}
              strokeWidth={0.5}
            />
          ))}

        {renderVectorGuide()}
        {interfaceObjects.map(renderInterfaceObject)}
        {entities.map(renderEntity)}
        {renderDraft()}
        

        {showCreationMarker && (
  <circle
    cx={hoverPoint.x * zoom + offsetX}
    cy={hoverPoint.y * zoom + offsetY}
    r={markerRadius}
    fill={markerColor}
    pointerEvents="none"
  />
)}
      </svg>
      

      {showFloatingInfo && (
        <div
          style={{
            position: "absolute",
            left: hoverScreenPoint.x + 14,
            top: hoverScreenPoint.y + 14,
            padding: "2px 6px",
            fontFamily: theme.fonts.body,
            fontSize: theme.fontSizes.xs,
            color: theme.colors.text.body,
            background: "rgba(255,255,255,0.96)",
            border: `1px solid ${theme.colors.borders.default}`,
            borderRadius: theme.borders.radius.sm,
            boxShadow: theme.shadows.xs,
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          {floatingText}
        </div>
      )}

      <div
        style={{
          position: "absolute",
          left: 10,
          right: 10,
          bottom: 10,
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          pointerEvents: "none",
          gap: 10,
        }}
      >
        <div
          style={{
            justifySelf: "start",
            padding: "4px 8px",
            fontFamily: theme.fonts.body,
            fontSize: theme.fontSizes.xs,
            color: theme.colors.text.body,
            background: "rgba(255,255,255,0.92)",
            border: `1px solid ${theme.colors.borders.default}`,
            borderRadius: theme.borders.radius.md,
            boxShadow: theme.shadows.xs,
            whiteSpace: "nowrap",
          }}
        >
          {statusModeLabel}
        </div>

        <div
          style={{
            justifySelf: "center",
            minHeight: 0,
            padding:
              warningMessage || moveStatus
                ? "4px 10px"
                : "0",
            fontFamily: theme.fonts.body,
            fontSize: theme.fontSizes.xs,
            color: warningMessage
              ? "#6e4b00"
              : moveStatus
              ? theme.colors.text.body
              : "transparent",
            background: warningMessage
              ? "rgba(255, 243, 205, 0.96)"
              : moveStatus
              ? "rgba(255,255,255,0.92)"
              : "transparent",
            border: warningMessage
              ? "1px solid rgba(214, 174, 0, 0.45)"
              : moveStatus
              ? `1px solid ${theme.colors.borders.default}`
              : "none",
            borderRadius: theme.borders.radius.md,
            boxShadow:
              warningMessage || moveStatus ? theme.shadows.xs : "none",
            whiteSpace: "nowrap",
            textAlign: "center",
          }}
        >
          {warningMessage ? `⚠ ${warningMessage}` : moveStatus}
        </div>

        <div
          style={{
            justifySelf: "end",
            padding: "4px 8px",
            fontFamily: theme.fonts.body,
            fontSize: theme.fontSizes.xs,
            color: theme.colors.text.body,
            background: "rgba(255,255,255,0.92)",
            border: `1px solid ${theme.colors.borders.default}`,
            borderRadius: theme.borders.radius.md,
            boxShadow: theme.shadows.xs,
            whiteSpace: "nowrap",
          }}
        >
          {statusCoordinates}
        </div>
      </div>
    </div>
  );
};

export default DrawViewport;